import * as React from "react";
import { useState } from "react";

import {
  Column,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";

import { ArrowSortIcon } from "@/assets/icons";
import PaginationComponent from "@/components/ui/pagination";
import { cn } from "@/lib/utils";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import { ExtendedColumnDef } from "../spatialAnalysis/spatialAnalysisTable/columns";

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={cn("w-full caption-bottom text-sm", className)}
    {...props}
  />
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("sticky top-0 [&_tr]:border-b border-[#E5E7EB]", className)}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn(className)} {...props} />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-neutral-100/50 font-medium [&>tr]:last:border-b-0 dark:bg-neutral-800/50",
      className
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-[#E5E7EB] transition-colors hover:bg-neutral-100/50 data-[state=selected]:bg-neutral-100 [&_td]:border-r [&_td]:border-gainsboro [&_th]:border-r [&_th]:border-gainsboro [&_td:last-child]:border-0 [&_th:last-child]:border-0",
      className
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-12 px-4 text-left align-middle font-medium text-neutral-500 [&:has([role=checkbox])]:pr-0 dark:text-neutral-400",
      className
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className)}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn(
      "mt-4 text-sm text-neutral-500 dark:text-neutral-400",
      className
    )}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

interface TableDataProps<TData, TValue> {
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];
}

function TableData<TData, TValue>(
  props: Readonly<TableDataProps<TData, TValue>>
) {
  const { columns, data } = props;

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(data.length / 100),
    initialState: {
      pagination: {
        pageSize: 100
      }
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting
    }
  });

  const getColumnWidth = (column: ExtendedColumnDef<TData, TValue>) => {
    if (column.maxWidth) {
      return `w-[${column.maxWidth}px] min-w-[${column.maxWidth}px] max-w-[${column.maxWidth}px]`;
    }
    return "w-full min-w-[200px]";
  };

  return (
    <div className="flex flex-col">
      <div className="max-h-[480px] overflow-hidden flex flex-col border border-gainsboro rounded-md">
        <Table className="rounded-md border">
          <TableHeader className="sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const column = header.column.columnDef as ExtendedColumnDef<
                    TData,
                    TValue
                  >;
                  return (
                    <TableHead
                      key={header.id}
                      className={twMerge(
                        "bg-white-smoke py-2 px-3",
                        getColumnWidth(column)
                      )}
                      style={
                        column.maxWidth
                          ? { width: `${column.maxWidth}px` }
                          : undefined
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => {
                    const column = cell.column.columnDef as ExtendedColumnDef<
                      TData,
                      TValue
                    >;
                    const content = flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    );
                    return (
                      <TableCell
                        key={cell.id}
                        className={twMerge("px-3", getColumnWidth(column))}
                        style={
                          column.maxWidth
                            ? { maxWidth: `${column.maxWidth}px` }
                            : undefined
                        }
                      >
                        <div className="truncate">{content}</div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-row items-center mt-4">
        <div className="text-txtBody text-sm ml-auto mr-3">
          Showing {(table.getState().pagination.pageIndex + 1) * 100} of{" "}
          {data.length} entries
        </div>
        <PaginationComponent
          currentPage={table.getState().pagination.pageIndex + 1}
          totalPages={table.getPageCount()}
          onPageChange={table.setPageIndex}
        />
      </div>
    </div>
  );
}

TableCaption.displayName = "TableData";

const TableSortedHeader = <TData,>({
  column,
  title
}: {
  readonly column: Column<TData>;
  readonly title: string;
}) => (
  <Button
    variant="ghost"
    className="flex flex-row items-center p-0 h-0"
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
  >
    <p className="text-txtBody text-xs font-semibold">{title}</p>
    <ArrowSortIcon width={16} height={16} />
  </Button>
);

TableSortedHeader.displayName = "TableSortedHeader";

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableData,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableSortedHeader
};
